<template>
    <div
        v-if="shown.length"
        class="ask-references"
        data-testid="ask-box-references"
    >
        <!-- `@click.exact`, because RouterLink ignores modified clicks and
             lets the browser open a new tab. A plain listener would fire
             anyway and dismiss the box behind the tab the reader opened
             precisely in order to keep it. -->
        <component
            :is="reference.to ? RouterLink : 'span'"
            v-for="reference in shown"
            :key="reference.key"
            :to="reference.to"
            class="ask-references__item"
            :class="{ 'ask-references__item--flat': !reference.to }"
            :data-testid="
                reference.to ? 'ask-box-reference' : 'ask-box-reference-flat'
            "
            @click.exact="reference.to && emit('navigate')"
        >
            <i :class="entityIcon(reference.entityType)" aria-hidden="true" />
            <span>{{ reference.name }}</span>
        </component>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { RouterLink, useRoute } from "vue-router";

import { entityIcon } from "@/components/Thread/reconcile/entity-icons";
import type { AskReference } from "@/lib/openapi/gen/types.gen";
import { withThreadRevision } from "@/lib/thread-revision-link";
import { entityLocation } from "@/router/entity-location";

/**
 * The entities an answer is about, as a row of links beneath it.
 *
 * With the page untouched, this row is the only thing connecting an answer to
 * its subject — so the epic's rule governs it: "a wrong link is worse than no
 * link, because it looks authoritative." Two halves of that are already done
 * before anything reaches here. The backend put every id through the same stek
 * gate the read tools use, so an id that arrives exists and is readable by this
 * user; and `entityLocation` refuses the four types that have no addressable
 * page rather than guessing at one.
 *
 * What is left is this component's share: a reference it cannot place renders
 * as **plain text with its icon**, not as a link to nowhere. The reader still
 * learns what the answer was about, which is most of the value, and nothing
 * invites a click that would go wrong.
 *
 * Its own component rather than markup inside `AskBox.vue`, and that is a test
 * constraint as much as a tidiness one: `AskBox.spec.ts` mounts with no router
 * at all, and `withThreadRevision` reaches the release store (and through it
 * PrimeVue's `useDialog`). Keeping both behind this boundary leaves that
 * harness alone.
 */
const props = defineProps<{ references: AskReference[] }>();

const emit = defineEmits<{ navigate: [] }>();

const route = useRoute();

const shown = computed(() => {
    // The backend caps and filters references but does not deduplicate them, so
    // a model that names the same thing twice would otherwise produce duplicate
    // Vue keys and two identical chips.
    const seen = new Set<string>();
    return props.references.flatMap((reference) => {
        const key = `${reference.entity_type}:${reference.entity_id}`;
        if (seen.has(key)) {
            return [];
        }
        seen.add(key);

        const to = entityLocation(reference, {
            orgName: route.params.orgName,
            stekName: route.params.stekName,
        });
        return [
            {
                key,
                name: reference.entity_name,
                entityType: reference.entity_type,
                // Wrapped here rather than inside `entityLocation`, per the
                // convention at every other link site. A mode-agnostic surface
                // calling a design-mode-only helper looks odd, but it is inert
                // unless a thread branch is being viewed — and that only
                // happens in design mode.
                to: to ? withThreadRevision(to) : undefined,
            },
        ];
    });
});
</script>

<style scoped>
.ask-references {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
}

/* Quiet by default: the answer is the content and these are its footnotes.
   They earn colour on hover rather than competing for attention at rest. */
.ask-references__item {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.2rem 0.55rem;
    border: 1px solid var(--p-surface-300);
    border-radius: 1rem;
    color: var(--p-text-muted-color);
    font-size: 0.8rem;
    text-decoration: none;
    transition:
        color 0.15s,
        border-color 0.15s;
}

a.ask-references__item:hover {
    color: var(--p-primary-color);
    border-color: var(--p-primary-color);
}

/* A reference with nowhere to go. Same shape so the row stays even, but no
   pointer and no hover: nothing here suggests it can be clicked. */
.ask-references__item--flat {
    cursor: default;
    opacity: 0.75;
}

.ask-references__item i {
    font-size: 0.75rem;
}
</style>
